import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './ApplicantView.module.css';
import { applySkillOrder } from '../../../shared/utils/skillOrder';
import FadeImage from '../../../shared/components/FadeImage';
import ActorDetailModal from '../components/ActorDetailModal';
import UpgradeAccountBanner from '../../../shared/components/UpgradeAccountBanner';
import { getApplicationsByPost, changeApplicationStatus, getApplicationRating } from '../../../api/applicationApi';
import { getTalentDetail } from '../../../api/talentApi';
import { getCastingDetail } from '../../../api/castingApi';
import LoadingSpinner from '../../../shared/components/LoadingSpinner';
import AiRecommendations, { ApplicantViewSwitch } from './AiRecommendations';
import { useAiSearchAvailability } from '../../Talents/useAiSearchAvailability';
import { AI_RECOMMEND_MOCK } from '../../../api/recommendationApi';


/* ── 기본 상수 (동적 단계 로드 전 fallback) ── */
export const STAGE_ORDER = ['지원 완료', '1차 통과', '2차 통과', '합격'];

export const STAGE_TO_STATUS = {
  '지원 완료': 'SUBMITTED',
  '1차 통과': 'PASS_1',
  '2차 통과': 'PASS_2',
  '합격': 'HIRED',
  '탈락': 'REJECTED',
};

export const STATUS_TO_STAGE = {
  SUBMITTED: '지원 완료',
  PASS_1: '1차 통과',
  PASS_2: '2차 통과',
  PASS_3: '2차 통과',
  PASS_4: '2차 통과',
  HIRED: '합격',
  REJECTED: '탈락',
};

/* ── 동적 단계 맵 빌더 ── */
function buildStageMaps(stages) {
  // stages: [{ stageNumber, stageName }] sorted by stageNumber
  const sorted = [...stages].sort((a, b) => a.stageNumber - b.stageNumber);
  const stageOrder = ['지원 완료', ...sorted.map(s => s.stageName), '합격'];
  const stageToStatus = { '지원 완료': 'SUBMITTED', '합격': 'HIRED', '탈락': 'REJECTED' };
  const statusToStage = { SUBMITTED: '지원 완료', HIRED: '합격', REJECTED: '탈락' };
  sorted.forEach((s) => {
    const passKey = `PASS_${s.stageNumber}`;
    stageToStatus[s.stageName] = passKey;
    statusToStage[passKey] = s.stageName;
  });
  return { stageOrder, stageToStatus, statusToStage };
}

/* ── 아이콘 ── */
function IcoGrid({ active }) {
  const c = active ? '#090909' : '#888896';
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <rect x="3" y="3" width="7" height="7" rx="1.5" stroke={c} strokeWidth="1.5"/>
      <rect x="14" y="3" width="7" height="7" rx="1.5" stroke={c} strokeWidth="1.5"/>
      <rect x="3" y="14" width="7" height="7" rx="1.5" stroke={c} strokeWidth="1.5"/>
      <rect x="14" y="14" width="7" height="7" rx="1.5" stroke={c} strokeWidth="1.5"/>
    </svg>
  );
}
function IcoListView({ active }) {
  const c = active ? '#090909' : '#888896';
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" stroke={c} strokeWidth="2" strokeLinecap="round"/>
    </svg>
  );
}
function IcoHeart({ filled, size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={filled ? '#E94934' : '#E9E9EC'}>
      <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/>
    </svg>
  );
}
function IcoStar({ filled }) {
  return (
    <svg width="13" height="13" viewBox="0 0 20 20">
      <path d="M10 1.5l2.5 5.1 5.6.8-4.1 4 .97 5.6L10 14.3l-4.97 2.7.97-5.6-4.1-4 5.6-.8L10 1.5z" fill={filled ? '#FFD92B' : '#E9E9EC'}/>
    </svg>
  );
}
function IcoExternal() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
      <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" stroke="#090909" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M15 3h6v6M10 14L21 3" stroke="#090909" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}
function IcoTrash() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
      <path d="M3 6h18M19 6l-1 14H6L5 6M10 11v6M14 11v6M9 6V4h6v2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}
function IcoFilter() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
      <path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z" stroke="#090909" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

/* ── ConfirmModal ── */
function ConfirmModal({ nextStageName, count, names, isFinal, isFail, isRestore, onCancel, onConfirm }) {
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onCancel(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onCancel]);

  const title = isRestore ? '복원 확인' : isFail ? '탈락 처리 확인' : '단계 이동 확인';
  const bodyText = isRestore
    ? (count === 1 && names
        ? <><strong>{names}</strong> 배우를 기존 단계로 복원하시겠습니까?</>
        : <><strong>{count}명</strong>의 배우를 기존 단계로 복원하시겠습니까?</>)
    : isFail
      ? (count === 1 && names
          ? <><strong>{names}</strong> 배우를 탈락칸으로 이동하시겠습니까?</>
          : <><strong>{count}명</strong>의 배우를 탈락칸으로 이동하시겠습니까?</>)
      : <>선택한 지원자 <strong>{count}명</strong>을 <strong>"{nextStageName}"</strong>으로 이동하시겠습니까?</>;
  const confirmBg = isFail ? '#E94934' : isFinal ? '#E94934' : '#090909';
  const cancelLabel = isRestore ? '아니오' : '취소';
  const confirmLabel = isRestore ? '예' : isFail ? '이동' : isFinal ? '최종 합격 처리' : '확인';

  return (
    <div
      onClick={onCancel}
      style={{
        position: 'fixed', inset: 0, zIndex: 2000,
        background: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: '20px',
          padding: '32px 28px 24px', width: '400px', maxWidth: '90vw',
          boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
          fontFamily: 'Pretendard, sans-serif',
        }}
      >
        <h3 style={{ margin: '0 0 12px', fontSize: '18px', fontWeight: 700, letterSpacing: '-0.04em', color: '#090909' }}>
          {title}
        </h3>
        <p style={{ margin: '0 0 16px', fontSize: '14px', fontWeight: 400, letterSpacing: '-0.03em', color: '#444', lineHeight: '160%' }}>
          {bodyText}
        </p>
        {!isFail && !isRestore && (
          <p style={{ margin: '0 0 16px', fontSize: '13px', fontWeight: 400, letterSpacing: '-0.03em', color: '#888896', lineHeight: '150%' }}>
            이 단계로 이동하면 배우에게 알림이 전송됩니다.
          </p>
        )}
        {isFinal && (
          <div style={{
            background: '#FFF8E1', border: '1px solid #FFD92B',
            borderRadius: '10px', padding: '12px 14px', marginBottom: '16px',
          }}>
            <p style={{ margin: 0, fontSize: '13px', fontWeight: 600, letterSpacing: '-0.03em', color: '#8B6000', lineHeight: '160%' }}>
              ⚠ 최종 합격 처리 후에는 취소가 불가합니다.<br />
              합격 처리시 배우에게 알림을 전송합니다.
            </p>
          </div>
        )}
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button
            onClick={onCancel}
            style={{
              padding: '10px 20px', borderRadius: '10px',
              border: '1px solid #E9E9EC', background: '#F3F3F5',
              fontFamily: 'Pretendard, sans-serif', fontSize: '14px',
              fontWeight: 600, color: '#555', cursor: 'pointer', letterSpacing: '-0.03em',
            }}
          >
            {cancelLabel}
          </button>
          <button
            onClick={onConfirm}
            style={{
              padding: '10px 20px', borderRadius: '10px',
              border: 'none', background: confirmBg,
              fontFamily: 'Pretendard, sans-serif', fontSize: '14px',
              fontWeight: 700, color: '#fff', cursor: 'pointer', letterSpacing: '-0.03em',
            }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Toast ── */
function Toast({ message, onDone }) {
  useEffect(() => {
    const t = setTimeout(onDone, 2800);
    return () => clearTimeout(t);
  }, [onDone]);
  return (
    <div style={{
      position: 'fixed', bottom: '36px', left: '50%', transform: 'translateX(-50%)',
      zIndex: 3000, background: '#090909', color: '#fff',
      padding: '12px 24px', borderRadius: '100px',
      fontFamily: 'Pretendard, sans-serif', fontSize: '14px', fontWeight: 500,
      letterSpacing: '-0.03em', boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
      pointerEvents: 'none',
    }}>
      {message}
    </div>
  );
}

/* ── FilterPanel ── */
function FilterPanel({ onClose, onApply, sortOrder, onSortChange, currentFilter }) {
  const [localSort, setLocalSort] = useState(sortOrder);
  const [hearts, setHearts] = useState(currentFilter?.hearts ?? 0);
  const [works, setWorks] = useState(currentFilter?.works ?? 0);
  const [age, setAge] = useState(currentFilter?.age ?? { min: '', max: '' });
  const [height, setHeight] = useState(currentFilter?.height ?? { min: '', max: '' });
  const [weight, setWeight] = useState(currentFilter?.weight ?? { min: '', max: '' });

  const reset = () => { setLocalSort('latest'); setHearts(0); setWorks(0); setAge({ min: '', max: '' }); setHeight({ min: '', max: '' }); setWeight({ min: '', max: '' }); };
  const apply = () => { onSortChange(localSort); onApply({ hearts, works, age, height, weight }); onClose(); };

  return (
    <div
      className={styles.filterPanel}
      onClick={e => e.stopPropagation()}
    >
      {/* 스크롤 되는 필터 항목 영역 */}
      <div className={styles.filterPanelInner}>
        <div>
          <span className={styles.filterLabel}>정렬</span>
          <div className={styles.filterHeartsRow}>
            {[{ key: 'latest', label: '최신순' }, { key: 'hearts', label: '좋아요순' }].map(({ key, label }) => (
              <button key={key} onClick={() => setLocalSort(key)} className={localSort === key ? styles.filterHeartBtnActive : styles.filterHeartBtn}>{label}</button>
            ))}
          </div>
        </div>
        <div>
          <span className={styles.filterLabel}>좋아요 수</span>
          <div className={styles.filterHeartsRow}>
            {[0, 1, 2, 3, 4, 5].map(n => (
              <button key={n} onClick={() => setHearts(n)} className={hearts === n ? styles.filterHeartBtnActive : styles.filterHeartBtn}>
                {n === 0 ? '전체' : `${n}+`}
              </button>
            ))}
          </div>
        </div>
        <div>
          <span className={styles.filterLabel}>출연 작품 수</span>
          <div className={styles.filterRangeRow}>
            <input type="range" min="0" max="50" value={works} onChange={e => setWorks(Number(e.target.value))} className={styles.filterRangeInput} />
            <span className={styles.filterRangeValue}>{works}개 이상</span>
          </div>
        </div>
        <div>
          <span className={styles.filterLabel}>나이</span>
          <div className={styles.filterInputRow}>
            <input type="number" min="0" max="120" placeholder="최소" value={age.min} onChange={e => setAge(p => ({ ...p, min: e.target.value }))} className={styles.filterInput} />
            <span className={styles.filterSeparator}>~</span>
            <input type="number" min="0" max="120" placeholder="최대" value={age.max} onChange={e => setAge(p => ({ ...p, max: e.target.value }))} className={styles.filterInput} />
            <span className={styles.filterUnit}>세</span>
          </div>
        </div>
        <div>
          <span className={styles.filterLabel}>키</span>
          <div className={styles.filterInputRow}>
            <input type="number" min="0" max="220" placeholder="최소" value={height.min} onChange={e => setHeight(p => ({ ...p, min: e.target.value }))} className={styles.filterInput} />
            <span className={styles.filterSeparator}>~</span>
            <input type="number" min="0" max="220" placeholder="최대" value={height.max} onChange={e => setHeight(p => ({ ...p, max: e.target.value }))} className={styles.filterInput} />
            <span className={styles.filterUnit}>cm</span>
          </div>
        </div>
        <div>
          <span className={styles.filterLabel}>몸무게</span>
          <div className={styles.filterInputRow}>
            <input type="number" min="0" max="150" placeholder="최소" value={weight.min} onChange={e => setWeight(p => ({ ...p, min: e.target.value }))} className={styles.filterInput} />
            <span className={styles.filterSeparator}>~</span>
            <input type="number" min="0" max="150" placeholder="최대" value={weight.max} onChange={e => setWeight(p => ({ ...p, max: e.target.value }))} className={styles.filterInput} />
            <span className={styles.filterUnit}>kg</span>
          </div>
        </div>
      </div>

      {/* 항상 하단에 고정되는 버튼 */}
      <div className={styles.filterActions}>
        <button onClick={reset} className={styles.filterResetBtn}>필터 초기화</button>
        <button onClick={apply} className={styles.filterApplyBtn}>적용</button>
      </div>
    </div>
  );
}

/* ── ApplicantCard (grid) ── */
function ApplicantCard({ actor, checked, onCheck, onDetail }) {
  return (
    <div onClick={() => onDetail(actor)} className={styles.applicantCard}>
      <div onClick={e => { e.stopPropagation(); onCheck(); }} className={styles.applicantCardCheckbox}>
        <div style={{
          width: 30, height: 30, borderRadius: '50%',
          border: `2px solid ${checked ? '#FFD92B' : '#A9A9B2'}`,
          background: checked ? '#FFD92B' : 'rgba(255,255,255,0.8)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {checked && <svg width="13" height="13" viewBox="0 0 24 24" fill="none"><path d="M20 6L9 17l-5-5" stroke="#090909" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/></svg>}
        </div>
      </div>
      <div className={styles.applicantCardPhoto}>
        <FadeImage src={actor.profileImage} alt={actor.name} size="medium" placeholder="talent" width={180} height={225} />
        {actor.tags && actor.tags.length > 0 && (
          <div className={styles.applicantTagsOverlay}>
            {actor.tags.map(tag => <span key={tag} className={styles.applicantOverlayTag}>{tag}</span>)}
          </div>
        )}
      </div>
      <div className={styles.applicantCardInfo}>
        <div className={styles.applicantCardNameRow}>
          <span className={styles.applicantCardName}>{actor.name}</span>
          <div className={styles.heartsRow}>{[1,2,3,4,5].map(i => <IcoHeart key={i} filled={i <= actor.hearts} size={11} />)}</div>
        </div>
        <div className={styles.applicantCardMetaRow}>
          <span className={styles.applicantCardMetaText}>{actor.gender}</span>
          <span className={styles.applicantCardMetaText}>{actor.age}세</span>
          <span className={styles.applicantCardMetaText}>{actor.height}</span>
          <div className={styles.ratingInline}>
            <IcoStar filled />
            <span className={styles.ratingText}>{actor.rating}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── ApplicantRow (list) ── */
function ApplicantRow({ actor, checked, onCheck, onDetail, isHeader, onCheckAll, allChecked, someChecked }) {
  if (isHeader) {
    return (
      <div className={styles.applicantRowHeader}>
        <div className={`${styles.cellBase} ${styles.cellHeader}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div
            onClick={onCheckAll}
            style={{
              width: 22, height: 22, borderRadius: '50%', cursor: 'pointer',
              border: `2px solid ${allChecked ? '#FFD92B' : someChecked ? '#FFD92B' : '#A9A9B2'}`,
              background: allChecked ? '#FFD92B' : 'transparent',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            {allChecked && <svg width="11" height="11" viewBox="0 0 24 24" fill="none"><path d="M20 6L9 17l-5-5" stroke="#090909" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/></svg>}
            {someChecked && <div style={{ width: 10, height: 2, background: '#090909', borderRadius: 1 }} />}
          </div>
        </div>
        <div className={`${styles.cellBase} ${styles.cellHeader}`}>사진</div>
        <div className={`${styles.cellBase} ${styles.cellHeader} ${styles.cellLeft}`}>이름</div>
        <div className={`${styles.cellBase} ${styles.cellHeader}`}>성별</div>
        <div className={`${styles.cellBase} ${styles.cellHeader}`}>나이</div>
        <div className={`${styles.cellBase} ${styles.cellHeader}`}>키</div>
        <div className={`${styles.cellBase} ${styles.cellHeader}`}>리뷰</div>
        <div className={`${styles.cellBase} ${styles.cellHeader}`}>포트폴리오</div>
        <div className={`${styles.cellBase} ${styles.cellHeader}`}>평가</div>
      </div>
    );
  }
  return (
    <div className={styles.applicantRow}>
      <div className={styles.cellCheckboxCenter}>
        <div
          onClick={onCheck}
          style={{
            width: 25, height: 25, borderRadius: '50%',
            border: `2px solid ${checked ? '#FFD92B' : '#A9A9B2'}`,
            background: checked ? '#FFD92B' : '#FFFFFF',
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          {checked && <svg width="13" height="13" viewBox="0 0 24 24" fill="none"><path d="M20 6L9 17l-5-5" stroke="#090909" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/></svg>}
        </div>
      </div>
      <div className={styles.cellPhotoCenter}>
        <div className={styles.rowPhoto}>
          <FadeImage src={actor.profileImage} alt={actor.name} size="thumb" placeholder="talent" width={40} height={40} style={{ borderRadius: 'inherit' }} />
        </div>
      </div>
      <div className={styles.cellNameBody}>{actor.name}</div>
      <div className={`${styles.cellBase} ${styles.cellBody}`}>{actor.gender}</div>
      <div className={`${styles.cellBase} ${styles.cellBody}`}>{actor.age}세</div>
      <div className={`${styles.cellBase} ${styles.cellBody}`}>{actor.height}</div>
      <div className={`${styles.cellBase} ${styles.cellBody} ${styles.cellRating}`}>
        <IcoStar filled />
        <span className={styles.cellRatingText}>{actor.rating}</span>
      </div>
      <div className={`${styles.cellBase} ${styles.cellBody} ${styles.cellPortfolio}`} onClick={() => onDetail(actor)}>
        <span className={styles.cellPortfolioText}>보기</span>
        <IcoExternal />
      </div>
      <div className={`${styles.cellBase} ${styles.cellBody} ${styles.cellHearts}`}>
        {[1,2,3,4,5].map(i => <IcoHeart key={i} filled={i <= actor.hearts} />)}
      </div>
    </div>
  );
}

/* ── ApplicantView ── */
export default function ApplicantView({ project, onBack }) {
  const navigate = useNavigate();
  const [roleTab, setRoleTab] = useState(null);
  const [roleOrder, setRoleOrder] = useState([]);
  const [stageTab, setStageTab] = useState('지원 완료');
  const [viewMode, setViewMode] = useState('grid');
  const [sortOrder, setSortOrder] = useState('latest');
  const [checked, setChecked] = useState({});
  const [actorDetail, setActorDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  // 동적 단계 맵 (getCastingDetail 로드 후 교체)
  const [dynStageOrder, setDynStageOrder] = useState(STAGE_ORDER);
  const [dynStageToStatus, setDynStageToStatus] = useState(STAGE_TO_STATUS);
  const [dynStatusToStage, setDynStatusToStage] = useState(STATUS_TO_STAGE);
  const [stageData, setStageData] = useState({ '탈락': [], '지원 완료': [], '1차 통과': [], '2차 통과': [], '합격': [] });
  const [loading, setLoading] = useState(true);
  const [showFilter, setShowFilter] = useState(false);
  const [appliedFilter, setAppliedFilter] = useState(null);
  const filterRef = useRef(null);
  const [confirmNext, setConfirmNext] = useState(null);
  const [confirmFail, setConfirmFail] = useState(null); // { toMove } | null
  const [toast, setToast] = useState(null);
  // AI 추천 배우 — 쓸 수 있는 제작사에게만 보인다. 서버가 막으면(주인 아님·기능 꺼짐) 탭을 숨긴다.
  const { available: aiAvailable } = useAiSearchAvailability();
  const [view, setView] = useState('applicants');
  const [aiBlocked, setAiBlocked] = useState(false);
  const showAiTab = (aiAvailable || AI_RECOMMEND_MOCK) && !aiBlocked;
  const handleAiUnavailable = useCallback(() => { setAiBlocked(true); setView('applicants'); }, []);

  const handleRatingChange = (applicationId, rating) => {
    setStageData(prev => {
      const next = {};
      Object.entries(prev).forEach(([stage, actors]) => {
        next[stage] = actors.map(actor => actor.id === applicationId ? { ...actor, hearts: rating } : actor);
      });
      return next;
    });
  };

  useEffect(() => {
    setLoading(true);

    // 공고 단계 정보를 먼저 가져온 뒤 지원자 목록 로드
    getCastingDetail(project.id)
      .then(casting => {
        const stages = casting?.stages ?? [];
        if (stages.length > 0) {
          const { stageOrder, stageToStatus, statusToStage } = buildStageMaps(stages);
          setDynStageOrder(stageOrder);
          setDynStageToStatus(stageToStatus);
          setDynStatusToStage(statusToStage);
          return { stageOrder, stageToStatus, statusToStage };
        }
        return { stageOrder: STAGE_ORDER, stageToStatus: STAGE_TO_STATUS, statusToStage: STATUS_TO_STAGE };
      })
      .catch(() => ({ stageOrder: STAGE_ORDER, stageToStatus: STAGE_TO_STATUS, statusToStage: STATUS_TO_STAGE }))
      .then(({ stageOrder, statusToStage }) => {
        // stageData 초기 버킷을 동적 단계 기반으로 구성
        const emptyBuckets = Object.fromEntries(['탈락', ...stageOrder].map(s => [s, []]));

        return getApplicationsByPost(project.id).then(res => {
          const list = Array.isArray(res) ? res : (res?.content ?? []);
          const buckets = { ...emptyBuckets };

          list.forEach(a => {
            const profile = a.talentProfile ?? a.actorProfile ?? a;
            const medias = profile.medias || a.medias || [];
            const profileCloseUpUrl = medias.find((m) => m.photoType === 'PROFILE_CLOSE_UP' && m.url)?.url ?? null;
            const profileImage = profileCloseUpUrl ?? (medias.length === 0 ? (profile.profileImageUrl ?? profile.profileImage ?? a.profileImageUrl ?? a.profileImage ?? null) : null);
            const stage = statusToStage[a.status] ?? stageOrder[0];
            const heightVal = profile.height ?? profile.heightCm ?? a.height;
            const weightVal = profile.weight ?? profile.weightKg ?? a.weight;
            const talentProfileId = a.talentProfileId ?? a.talentId ?? profile.id ?? null;
            const genderRaw = profile.gender ?? a.gender ?? '';
            const genderKo = genderRaw === 'MALE' ? '남성' : genderRaw === 'FEMALE' ? '여성' : genderRaw || '—';
            const rawSkills = (profile.skills ?? []).map(s => s.name ?? s.skillName ?? s).filter(Boolean);
            const tags = applySkillOrder(talentProfileId, rawSkills);
            if (!buckets[stage]) buckets[stage] = [];
            buckets[stage].push({
              id: a.id, talentProfileId,
              name: profile.stageName || profile.name || a.actorName || a.name || '—',
              gender: genderKo,
              age: profile.age ?? a.age ?? 0,
              height: heightVal ? `${heightVal}cm` : '—',
              weight: weightVal ? `${weightVal}kg` : '—',
              rating: profile.rating ?? a.rating ?? 0,
              hearts: a.producerRating ?? 0,
              role: a.roleName ?? a.castingRoleName ?? a.role ?? '—',
              profileImage, tags,
              previousStage: a.previousStatus ? (statusToStage[a.previousStatus] ?? stageOrder[0]) : null,
            });
          });

          setStageData(buckets);
          const uniqueRoles = [...new Set(list.map(a => a.roleName ?? a.castingRoleName ?? a.role ?? ''))].filter(Boolean);
          setRoleOrder(uniqueRoles);
          if (uniqueRoles.length > 0) setRoleTab(uniqueRoles[0]);

          const allActorIds = list.map(a => a.id);
          Promise.allSettled(
            allActorIds.map(id => getApplicationRating(id).then(res => ({ id, rating: res?.rating ?? 0 })))
          ).then(results => {
            const ratingMap = {};
            results.forEach(r => { if (r.status === 'fulfilled' && r.value.rating) ratingMap[r.value.id] = r.value.rating; });
            if (Object.keys(ratingMap).length === 0) return;
            setStageData(prev => {
              const next = {};
              Object.entries(prev).forEach(([stage, actors]) => {
                next[stage] = actors.map(a => ratingMap[a.id] != null ? { ...a, hearts: ratingMap[a.id] } : a);
              });
              return next;
            });
          });

          const allActors = Object.values(buckets).flat();
          const toFetch = allActors.filter(a => a.talentProfileId);
          if (toFetch.length > 0) {
            Promise.allSettled(
              toFetch.map(a => getTalentDetail(a.talentProfileId).then(full => ({ applicationId: a.id, full })))
            ).then(results => {
              setStageData(prev => {
                const detailMap = {};
                results.forEach(r => { if (r.status === 'fulfilled') detailMap[r.value.applicationId] = r.value.full; });
                const next = {};
                Object.entries(prev).forEach(([stage, actors]) => {
                  next[stage] = actors.map(a => {
                    const full = detailMap[a.id];
                    if (!full) return a;
                    const gRaw = full.gender ?? '';
                    const gKo = gRaw === 'MALE' ? '남성' : gRaw === 'FEMALE' ? '여성' : gRaw || a.gender;
                    const rawSkills = (full.skills ?? []).map(s => s.name ?? s.skillName ?? s).filter(Boolean);
                    const tags = rawSkills.length > 0 ? applySkillOrder(a.talentProfileId, rawSkills) : a.tags;
                    return { ...a, name: full.stageName || full.name || a.name, gender: gKo, tags };
                  });
                });
                return next;
              });
            });
          }
        });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [project.id]);

  const handleOpenDetail = async (actor) => {
    if (!actor.talentProfileId) { setActorDetail({ ...actor, applicationId: actor.id, initialHearts: actor.hearts ?? 0 }); return; }
    setDetailLoading(true);
    try {
      const full = await getTalentDetail(actor.talentProfileId);
      const medias = full.medias || [];
      const gRaw = full.gender ?? '';
      const gKo = gRaw === 'MALE' ? '남성' : gRaw === 'FEMALE' ? '여성' : gRaw || actor.gender || '—';
      const rawSkills = (full.skills ?? []).map(s => s.name ?? s.skillName ?? s).filter(Boolean);
      const tags = rawSkills.length > 0 ? applySkillOrder(actor.talentProfileId, rawSkills) : (actor.tags ?? []);
      setActorDetail({
        ...full,
        name: full.stageName || full.name || actor.name,
        age: full.age ?? actor.age,
        height: full.heightCm ? `${full.heightCm}cm` : actor.height,
        weight: full.weightKg ? `${full.weightKg}kg` : actor.weight,
        gender: gKo, tags, role: actor.role,
        applicationId: actor.id,
        initialHearts: actor.hearts ?? 0,
        talentProfileId: actor.talentProfileId,
        profileImageUrl:
          medias.find((m) => m.photoType === 'PROFILE_CLOSE_UP' && m.url)?.url ??
          (medias.length === 0 ? (full.profileImageUrl ?? full.profileImage ?? actor.profileImage ?? null) : null),
      });
    } catch {
      setActorDetail(actor);
    } finally {
      setDetailLoading(false);
    }
  };

  const toggleCheck = (id) => setChecked(p => ({ ...p, [id]: !p[id] }));
  const checkedIds = Object.entries(checked).filter(([, v]) => v).map(([k]) => Number(k));
  const anyChecked = checkedIds.length > 0;

  const ro = (word) => {
    const code = word.charCodeAt(word.length - 1) - 0xAC00;
    return code >= 0 && code % 28 !== 0 ? '으로' : '로';
  };

  const nextStageLabel = () => {
    const idx = dynStageOrder.indexOf(stageTab);
    if (idx < dynStageOrder.length - 1) { const s = dynStageOrder[idx + 1]; return `${s}${ro(s)} 이동`; }
    return null;
  };

  const prevStageLabel = () => {
    const idx = dynStageOrder.indexOf(stageTab);
    if (idx > 0) { const s = dynStageOrder[idx - 1]; return `${s}${ro(s)} 이동`; }
    return null;
  };

  const moveToNext = () => {
    const idx = dynStageOrder.indexOf(stageTab);
    if (idx >= dynStageOrder.length - 1) return;
    const next = dynStageOrder[idx + 1];
    const toMove = stageData[stageTab].filter(a => checkedIds.includes(a.id));
    if (toMove.length === 0) return;
    setConfirmNext({ nextStage: next, toMove });
  };

  const executeMoveToNext = () => {
    if (!confirmNext) return;
    const { nextStage: next, toMove } = confirmNext;
    setConfirmNext(null);
    const nextStatus = dynStageToStatus[next];
    const toMoveIds = new Set(toMove.map(a => a.id));
    const remaining = stageData[stageTab].filter(a => !toMoveIds.has(a.id));
    setStageData(p => ({ ...p, [stageTab]: remaining, [next]: [...p[next], ...toMove] }));
    setChecked({});
    setStageTab(next);
    setToast(`지원자가 "${next}"로 이동되었습니다.`);
    if (nextStatus) {
      Promise.allSettled(
        toMove.map(a => changeApplicationStatus(a.id, nextStatus))
      ).then(results => {
        const failed = toMove.filter((_, i) => results[i].status === 'rejected');
        if (failed.length > 0) {
          setStageData(p => ({
            ...p,
            [next]: (p[next] || []).filter(x => !failed.some(f => f.id === x.id)),
            [stageTab]: [...(p[stageTab] || []), ...failed],
          }));
          setToast(`${failed.length}명 이동에 실패했습니다.`);
        }
      });
    }
  };

  const moveToPrev = () => {
    const idx = dynStageOrder.indexOf(stageTab);
    if (idx <= 0) return;
    const prev = dynStageOrder[idx - 1];
    const prevStatus = dynStageToStatus[prev];
    const toMove = stageData[stageTab].filter(a => checkedIds.includes(a.id));
    const remaining = stageData[stageTab].filter(a => !checkedIds.includes(a.id));
    setStageData(p => ({ ...p, [stageTab]: remaining, [prev]: [...p[prev], ...toMove] }));
    setChecked({});
    setStageTab(prev);
    if (prevStatus) {
      toMove.forEach(a =>
        changeApplicationStatus(a.id, prevStatus).catch(() => {
          setStageData(p => ({
            ...p,
            [prev]: (p[prev] || []).filter(x => x.id !== a.id),
            [stageTab]: [...(p[stageTab] || []), a],
          }));
        })
      );
    }
  };

  const openFailConfirm = () => {
    const toMove = stageData[stageTab].filter(a => checkedIds.includes(a.id));
    if (toMove.length === 0) return;
    setConfirmFail({ toMove });
  };

  const executeMoveToFail = () => {
    if (!confirmFail) return;
    const { toMove } = confirmFail;
    setConfirmFail(null);
    const toMoveIds = new Set(toMove.map(a => a.id));
    // previousStage를 저장해서 복원 시 원래 단계로 되돌릴 수 있게 함
    const withPrev = toMove.map(a => ({ ...a, previousStage: stageTab }));
    setStageData(p => ({
      ...p,
      [stageTab]: p[stageTab].filter(a => !toMoveIds.has(a.id)),
      '탈락': [...p['탈락'], ...withPrev],
    }));
    setChecked({});
    setToast(`${toMove.length}명이 탈락 처리되었습니다.`);
    Promise.allSettled(
      toMove.map(a => changeApplicationStatus(a.id, 'REJECTED'))
    ).then(results => {
      const failed = toMove.filter((_, i) => results[i].status === 'rejected');
      if (failed.length > 0) {
        const failedIds = new Set(failed.map(a => a.id));
        setStageData(p => ({
          ...p,
          '탈락': p['탈락'].filter(a => !failedIds.has(a.id)),
          [stageTab]: [...p[stageTab], ...failed],
        }));
        setToast(`${failed.length}명 탈락 처리에 실패했습니다.`);
      }
    });
  };

  const [confirmRestore, setConfirmRestore] = useState(null); // { toMove } | null

  const openRestoreConfirm = () => {
    const toMove = stageData['탈락'].filter(a => checkedIds.includes(a.id));
    if (toMove.length === 0) return;
    setConfirmRestore({ toMove });
  };

  const executeRestore = () => {
    if (!confirmRestore) return;
    const { toMove } = confirmRestore;
    setConfirmRestore(null);
    const toMoveIds = new Set(toMove.map(a => a.id));
    const fallback = dynStageOrder[0] ?? '지원 완료';
    setStageData(p => {
      const next = { ...p, '탈락': p['탈락'].filter(a => !toMoveIds.has(a.id)) };
      toMove.forEach(a => {
        const dest = a.previousStage ?? fallback;
        next[dest] = [...(next[dest] || []), a];
      });
      return next;
    });
    setChecked({});
    setToast(`${toMove.length}명이 복원되었습니다.`);
    Promise.allSettled(
      toMove.map(a => {
        const dest = a.previousStage ?? fallback;
        const status = dynStageToStatus[dest] ?? 'SUBMITTED';
        return changeApplicationStatus(a.id, status);
      })
    ).then(results => {
      const failed = toMove.filter((_, i) => results[i].status === 'rejected');
      if (failed.length > 0) {
        const failedIds = new Set(failed.map(a => a.id));
        setStageData(p => {
          const next = { ...p };
          failed.forEach(a => {
            const dest = a.previousStage ?? '지원 완료';
            next[dest] = (next[dest] || []).filter(x => !failedIds.has(x.id));
          });
          next['탈락'] = [...p['탈락'], ...failed];
          return next;
        });
        setToast(`${failed.length}명 복원에 실패했습니다.`);
      }
    });
  };

  const stageList = stageData[stageTab] || [];
  const allRoles = roleOrder.length > 0
    ? roleOrder
    : [...new Set(Object.values(stageData).flat().map(a => a.role).filter(Boolean))];
  const roleList = roleTab ? stageList.filter(a => a.role === roleTab) : stageList;
  const filteredList = appliedFilter ? roleList.filter(a => {
    if (appliedFilter.hearts > 0 && a.hearts < appliedFilter.hearts) return false;
    if (appliedFilter.works > 0 && (a.works ?? 0) < appliedFilter.works) return false;
    if (appliedFilter.age.min !== '' && a.age < Number(appliedFilter.age.min)) return false;
    if (appliedFilter.age.max !== '' && a.age > Number(appliedFilter.age.max)) return false;
    if (appliedFilter.height.min !== '' && parseInt(a.height) < Number(appliedFilter.height.min)) return false;
    if (appliedFilter.height.max !== '' && parseInt(a.height) > Number(appliedFilter.height.max)) return false;
    if (appliedFilter.weight && appliedFilter.weight.min !== '' && parseInt(a.weight) < Number(appliedFilter.weight.min)) return false;
    if (appliedFilter.weight && appliedFilter.weight.max !== '' && parseInt(a.weight) > Number(appliedFilter.weight.max)) return false;
    return true;
  }) : roleList;

  const currentList = sortOrder === 'hearts'
    ? [...filteredList].sort((a, b) => b.hearts - a.hearts)
    : filteredList;

  const allChecked = currentList.length > 0 && currentList.every(a => checked[a.id]);
  const someChecked = !allChecked && currentList.some(a => checked[a.id]);

  const toggleAll = () => {
    if (allChecked) {
      setChecked({});
    } else {
      const next = {};
      currentList.forEach(a => { next[a.id] = true; });
      setChecked(next);
    }
  };

  const allApplicants = Object.values(stageData).flat();
  const roleCounts = allRoles.map(r => ({ id: r, count: allApplicants.filter(a => a.role === r).length }));
  const nextLabel = nextStageLabel();
  const prevLabel = prevStageLabel();

  return (
    <div className={styles.applicantViewWrapper} onClick={() => { setShowFilter(false); }}>
      {detailLoading && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '24px 40px', display: 'flex', justifyContent: 'center' }}><LoadingSpinner size="sm" /></div>
        </div>
      )}
      {confirmNext && (
        <ConfirmModal
          nextStageName={confirmNext.nextStage}
          count={confirmNext.toMove.length}
          isFinal={confirmNext.nextStage === dynStageOrder[dynStageOrder.length - 1]}
          onCancel={() => setConfirmNext(null)}
          onConfirm={executeMoveToNext}
        />
      )}
      {confirmFail && (
        <ConfirmModal
          isFail
          count={confirmFail.toMove.length}
          names={confirmFail.toMove.length === 1 ? confirmFail.toMove[0].name : null}
          onCancel={() => setConfirmFail(null)}
          onConfirm={executeMoveToFail}
        />
      )}
      {confirmRestore && (
        <ConfirmModal
          isRestore
          count={confirmRestore.toMove.length}
          names={confirmRestore.toMove.length === 1 ? confirmRestore.toMove[0].name : null}
          onCancel={() => setConfirmRestore(null)}
          onConfirm={executeRestore}
        />
      )}
      {toast && <Toast message={toast} onDone={() => setToast(null)} />}
      {actorDetail && (() => {
        const currentStageIdx = dynStageOrder.indexOf(stageTab);
        const nextStage = currentStageIdx < dynStageOrder.length - 1 ? dynStageOrder[currentStageIdx + 1] : null;
        const handleModalMoveToNext = nextStage ? () => {
          setActorDetail(null);
          setConfirmNext({ nextStage, toMove: [actorDetail] });
        } : undefined;
        return (
          <ActorDetailModal
            actor={actorDetail}
            applicationId={actorDetail.applicationId}
            actorList={currentList}
            onNavigate={(nextActor) => { handleOpenDetail(nextActor); }}
            onRatingChange={handleRatingChange}
            onClose={() => setActorDetail(null)}
            onMoveToNext={handleModalMoveToNext}
            nextStageName={nextStage}
          />
        );
      })()}

      <button onClick={onBack} className={styles.backBtn}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M19 12H5M12 19l-7-7 7-7" stroke="#A2A2A2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
        뒤로 가기
      </button>

      <div className={styles.applicantViewHeader}>
        <h1 className={styles.applicantViewTitle}>지원자 관리</h1>
        <div
          className={styles.applicantViewSubtitle}
          onClick={() => project.id != null && navigate(`/castings/${project.id}`)}
          style={{ cursor: project.id != null ? 'pointer' : 'default' }}
        >
          {project.title}
        </div>
      </div>

      {showAiTab && <ApplicantViewSwitch value={view} onChange={setView} />}

      {showAiTab && view === 'ai' ? (
        <AiRecommendations postId={project.id} onUnavailable={handleAiUnavailable} />
      ) : (<>
      <div className={styles.rolePillsRow}>
        {roleCounts.map(({ id, count }) => (
          <button
            key={id}
            onClick={() => { setRoleTab(id); setChecked({}); }}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              padding: '8px 18px', borderRadius: '100px', border: 'none', cursor: 'pointer',
              background: roleTab === id ? '#090909' : '#E9E9EC',
            }}
          >
            <span style={{ fontFamily: 'Pretendard, sans-serif', fontSize: '14px', fontWeight: 700, letterSpacing: '-0.04em', color: roleTab === id ? '#FFFFFF' : '#888896', lineHeight: '140%' }}>{id}</span>
            <span style={{ width: 20, height: 20, borderRadius: '50%', background: roleTab === id ? 'rgba(255,255,255,0.2)' : '#A9A9B2', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Pretendard, sans-serif', fontSize: '12px', fontWeight: 700, letterSpacing: '-0.04em', color: roleTab === id ? '#FFFFFF' : '#555555', lineHeight: '140%' }}>{count}</span>
          </button>
        ))}
      </div>

      {/* 대행 계정 — 마지막 단계(합격자 확정)를 보고 있을 때 한 번만 전환 권유 */}
      {stageTab === dynStageOrder[dynStageOrder.length - 1] && (
        <UpgradeAccountBanner id={`applicant-done-${project.id}`} />
      )}

      <div className={styles.stageTabsRow}>
        <div className={styles.stageTabsLeft}>
          {['탈락', ...dynStageOrder].map(s => {
            const cnt = roleTab
              ? (stageData[s] || []).filter(a => a.role === roleTab).length
              : (stageData[s]?.length || 0);
            const isActive = stageTab === s;
            return (
              <button
                key={s}
                onClick={() => { setStageTab(s); setChecked({}); }}
                style={{
                  padding: '12px 20px', background: 'none', border: 'none',
                  borderBottom: `2px solid ${isActive ? '#090909' : 'transparent'}`,
                  cursor: 'pointer', marginBottom: '-1px',
                  display: 'flex', alignItems: 'center', gap: '5px',
                  fontFamily: 'Pretendard, sans-serif', fontSize: '14px',
                  fontWeight: isActive ? 700 : 400, letterSpacing: '-0.04em',
                  color: isActive ? '#090909' : '#A2A2A2',
                  lineHeight: '140%',
                }}
              >
                {s} <span style={{ fontFamily: 'Pretendard, sans-serif', fontSize: '13px', fontWeight: isActive ? 700 : 400, letterSpacing: '-0.04em', color: isActive ? '#090909' : '#A2A2A2', lineHeight: '140%' }}>{cnt}</span>
              </button>
            );
          })}
        </div>
        <div className={styles.stageTabsRight}>
          {stageTab === '탈락' ? (
            <button
              onClick={openRestoreConfirm}
              disabled={!anyChecked}
              style={{
                padding: '10px 20px', borderRadius: '10px',
                border: anyChecked ? 'none' : '1px solid #E9E9EC',
                background: anyChecked ? '#090909' : '#E9E9EC',
                cursor: anyChecked ? 'pointer' : 'default',
                fontFamily: 'Pretendard, sans-serif', fontSize: '14px', fontWeight: 700,
                letterSpacing: '-0.04em', color: anyChecked ? '#FFFFFF' : '#A2A2A2', lineHeight: '140%',
              }}
            >
              {anyChecked ? `${checkedIds.length}명 복원` : '복원'}
            </button>
          ) : (
            <>
              {prevLabel && (
                <button
                  onClick={moveToPrev}
                  disabled={!anyChecked}
                  style={{
                    padding: '10px 20px', borderRadius: '10px',
                    border: `1px solid ${anyChecked ? '#D9D9DF' : '#E9E9EC'}`,
                    background: anyChecked ? '#F3F3F5' : '#FAFAFB',
                    cursor: anyChecked ? 'pointer' : 'default',
                    fontFamily: 'Pretendard, sans-serif', fontSize: '14px', fontWeight: 700,
                    letterSpacing: '-0.04em', color: anyChecked ? '#55555F' : '#B5B5BF', lineHeight: '140%',
                  }}
                >
                  {prevLabel}
                </button>
              )}
              {nextLabel && (
                <button
                  onClick={moveToNext}
                  disabled={!anyChecked}
                  style={{
                    padding: '10px 20px', borderRadius: '10px',
                    border: anyChecked ? 'none' : '1px solid #E9E9EC',
                    background: anyChecked ? '#FFD92B' : '#E9E9EC',
                    cursor: anyChecked ? 'pointer' : 'default',
                    fontFamily: 'Pretendard, sans-serif', fontSize: '14px', fontWeight: 700,
                    letterSpacing: '-0.04em', color: anyChecked ? '#090909' : '#A2A2A2', lineHeight: '140%',
                  }}
                >
                  {nextLabel}
                </button>
              )}
              <button
                onClick={openFailConfirm}
                disabled={!anyChecked}
                style={{
                  width: 38, height: 38, borderRadius: '10px', border: '1px solid #E9E9EC',
                  background: anyChecked ? '#FFF0EE' : '#FAFAFB',
                  cursor: anyChecked ? 'pointer' : 'default',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: anyChecked ? '#E94934' : '#C5C5CF',
                  transition: 'background 0.15s, color 0.15s',
                }}
              >
                <IcoTrash />
              </button>
            </>
          )}
          <div className={styles.viewToggleGroup}>
            {[['grid', <IcoGrid active={viewMode === 'grid'} />], ['list', <IcoListView active={viewMode === 'list'} />]].map(([mode, icon]) => (
              <button
                key={mode}
                onClick={() => setViewMode(mode)}
                style={{
                  width: 34, height: 34, borderRadius: '6px', border: 'none', cursor: 'pointer',
                  background: viewMode === mode ? '#FFFFFF' : 'transparent',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: viewMode === mode ? '2px 2px 10px rgba(0,0,0,0.08)' : 'none',
                }}
              >
                {icon}
              </button>
            ))}
          </div>
          <div className={styles.filterBtnWrapper} ref={filterRef}>
            <button
              onClick={e => {
                e.stopPropagation();
                setShowFilter(p => !p);
              }}
              className={styles.filterBtn}
            >
              <IcoFilter />
              필터 · {sortOrder === 'hearts' ? '좋아요순' : '최신순'}
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M6 9l6 6 6-6" stroke="#888896" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </button>
            {showFilter && (
              <FilterPanel
                onClose={() => setShowFilter(false)}
                onApply={setAppliedFilter}
                sortOrder={sortOrder}
                onSortChange={setSortOrder}
                currentFilter={appliedFilter}
              />
            )}
          </div>
        </div>
      </div>

      {!loading && currentList.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 2px', marginBottom: '4px' }}>
          <div
            onClick={toggleAll}
            style={{
              width: 22, height: 22, borderRadius: '50%', cursor: 'pointer', flexShrink: 0,
              border: `2px solid ${allChecked ? '#FFD92B' : someChecked ? '#FFD92B' : '#A9A9B2'}`,
              background: allChecked ? '#FFD92B' : 'transparent',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            {allChecked && <svg width="11" height="11" viewBox="0 0 24 24" fill="none"><path d="M20 6L9 17l-5-5" stroke="#090909" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/></svg>}
            {someChecked && <div style={{ width: 10, height: 2, background: '#090909', borderRadius: 1 }} />}
          </div>
          <span style={{ fontFamily: 'Pretendard, sans-serif', fontSize: '13px', fontWeight: 500, color: '#555', letterSpacing: '-0.03em', cursor: 'pointer', userSelect: 'none' }} onClick={toggleAll}>
            {allChecked ? '전체 해제' : `전체선택 (${currentList.length}명)`}
          </span>
          {anyChecked && (
            <span style={{ fontFamily: 'Pretendard, sans-serif', fontSize: '13px', color: '#A2A2A2', letterSpacing: '-0.03em' }}>
              · {checkedIds.length}명 선택됨
            </span>
          )}
        </div>
      )}

      {loading ? (
        <div className={styles.gridEmpty} style={{ display: 'flex', justifyContent: 'center' }}><LoadingSpinner size="md" /></div>
      ) : viewMode === 'grid' ? (
        <div className={styles.gridContent}>
          {currentList.map(actor => (
            <ApplicantCard key={actor.id} actor={actor} checked={!!checked[actor.id]} onCheck={() => toggleCheck(actor.id)} onDetail={handleOpenDetail} />
          ))}
          {currentList.length === 0 && (
            <div className={styles.gridEmpty}>
              <span className={styles.emptyText}>해당 단계의 지원자가 없습니다.</span>
            </div>
          )}
        </div>
      ) : (
        <div className={styles.listContainer}>
          <ApplicantRow isHeader onCheckAll={toggleAll} allChecked={allChecked} someChecked={someChecked} />
          {currentList.map(actor => (
            <ApplicantRow key={actor.id} actor={actor} checked={!!checked[actor.id]} onCheck={() => toggleCheck(actor.id)} onDetail={handleOpenDetail} />
          ))}
          {currentList.length === 0 && (
            <div className={styles.listEmpty}>
              <span className={styles.emptyText}>해당 단계의 지원자가 없습니다.</span>
            </div>
          )}
        </div>
      )}
      </>)}
    </div>
  );
}
